'use client';

import { PAGE_QUERYResult } from '@/sanity.types';
import { useEffect } from 'react';

type FormBuilderProps = Extract<
  NonNullable<NonNullable<PAGE_QUERYResult>['content']>[number],
  { _type: 'thirdPartyForms' }
>;

export default function ThirdPartyFormLandingPage(formData: FormBuilderProps) {
  useEffect(() => {
    if (!formData?.code?.code || !formData?.code?.language) return;

    const { code, language } = formData.code;

    if (language === 'javascript') {
      // Handle JavaScript execution
      if (code.includes('<script')) {
        // Handle script tag case
        const parser = new DOMParser();
        const doc = parser.parseFromString(code, 'text/html');
        const scripts = doc.querySelectorAll('script');
        const addedScripts: HTMLScriptElement[] = [];

        scripts.forEach((originalScript) => {
          // Check if script already exists in the DOM
          let scriptExists = false;

          if (originalScript.src) {
            // Check by src attribute
            scriptExists =
              document.querySelector(`script[src="${originalScript.src}"]`) !==
              null;
          } else if (originalScript.innerHTML.trim()) {
            // Check by content for inline scripts
            const existingScripts = document.querySelectorAll('script');
            scriptExists = Array.from(existingScripts).some(
              (script) =>
                script.innerHTML.trim() === originalScript.innerHTML.trim()
            );
          }

          if (!scriptExists) {
            const script = document.createElement('script');
            if (originalScript.src) {
              script.src = originalScript.src;
            }
            if (originalScript.innerHTML) {
              script.innerHTML = originalScript.innerHTML;
            }
            script.defer = true;
            document.body.appendChild(script);
            addedScripts.push(script);
          }
        });

        // Cleanup only the scripts we added
        return () => {
          addedScripts.forEach((script) => {
            if (script && script.parentNode) {
              script.parentNode.removeChild(script);
            }
          });
        };
      } else {
        // Handle inline JavaScript
        try {
          // Check if this exact inline script already exists
          const existingScripts = document.querySelectorAll('script');
          const scriptExists = Array.from(existingScripts).some(
            (script) => script.innerHTML.trim() === code.trim()
          );

          if (!scriptExists) {
            // Create a new script element for inline JavaScript
            const script = document.createElement('script');
            script.innerHTML = code;
            script.defer = true;
            document.body.appendChild(script);

            // Cleanup for inline script
            return () => {
              if (script && script.parentNode) {
                script.parentNode.removeChild(script);
              }
            };
          }
        } catch (error) {
          console.error('Error executing JavaScript:', error);
        }
      }
    }
  }, [formData]);

  if (!formData?.code?.code) {
    return null;
  }

  // For HTML language or when the code contains HTML content, render it directly
  if (formData.code.language === 'html') {
    return (
      <div
        id={formData._key}
        className="third-party-form"
        dangerouslySetInnerHTML={{
          __html: formData.code.code,
        }}
      />
    );
  }

  // For JavaScript, only render if there's visible content (script tags)
  if (
    formData.code.language === 'javascript' &&
    formData.code.code.includes('<script')
  ) {
    return (
      <div
        id={formData._key}
        className="third-party-form"
        dangerouslySetInnerHTML={{
          __html: formData.code.code,
        }}
      />
    );
  }

  return null;
}
